import React, { useState, useEffect } from 'react';
import { CheckCircle, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { 
  organizationService, 
  locationService, 
  buildingService, 
  floorService, 
  departmentService, 
  roomService, 
  bedService 
} from '../../../services/firebaseService';
import { userService } from '../../../services/firestoreService';
import { biologicalEntityService } from '../../../services/firebaseService';
import { usePolicyFrameworks } from '../../../hooks/usePolicyFrameworks';
import { useProcesses } from '../../../hooks/useProcesses';
import { useProtocols } from '../../../hooks/useProtocols';
import './SetupWizardSteps.css';

const Step12Review = ({ stepData, onComplete, onValidation, structure }) => {
  const { t } = useTranslation('setup');
  const [reviewData, setReviewData] = useState({
    organization: null,
    clinicalStructure: null,
    users: null,
    biologicalEntities: null,
    policyFrameworks: null,
    processes: null,
    protocols: null
  });
  const [loading, setLoading] = useState(true);
  const organizationId = structure?.organizations?.[0]?.id || null;

  const { frameworks } = usePolicyFrameworks(organizationId);
  const { processes } = useProcesses(organizationId);
  const { protocols } = useProtocols({});

  useEffect(() => {
    const loadReviewData = async () => {
      try {
        const [
          organizations,
          locations,
          buildings,
          floors,
          departments,
          rooms,
          beds,
          users,
          entities
        ] = await Promise.all([
          organizationService.getAll(),
          locationService.getAll(),
          buildingService.getAll(),
          floorService.getAll(),
          departmentService.getAll(),
          roomService.getAll(),
          bedService.getAll(),
          userService.getAll(),
          biologicalEntityService.getAll()
        ]);

        setReviewData({
          organization: organizations?.[0] || null,
          clinicalStructure: {
            locations: locations?.length || 0,
            buildings: buildings?.length || 0,
            floors: floors?.length || 0,
            departments: departments?.length || 0,
            rooms: rooms?.length || 0,
            beds: beds?.length || 0
          },
          users: {
            total: users?.length || 0,
            management: users?.filter(u => u.role_id === 'management').length || 0,
            icPersonnel: users?.filter(u => u.role_id === 'ic_personnel').length || 0,
            administration: users?.filter(u => u.role_id === 'administration').length || 0
          },
          biologicalEntities: entities?.length || 0,
          policyFrameworks: frameworks?.length || 0,
          processes: processes?.length || 0,
          protocols: protocols?.length || 0
        });
      } catch (error) {
        console.error('Error loading review data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadReviewData();
  }, [frameworks, processes, protocols]);

  const getStatus = (condition) => condition ? 'complete' : 'incomplete';

  if (loading) {
    return (
      <div className="step-content">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>{t('step12.loadingText')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="step-content">
      <div className="step-section">
        <h3>{t('step12.title')}</h3>
        <p>{t('step12.description')}</p>
      </div>

      <div className="review-sections">
        <div className="review-section">
          <h4>{t('step12.sections.clinicalStructure')}</h4>
          <div className="review-items">
            <div className={`review-item ${getStatus(reviewData.organization)}`}>
              {reviewData.organization ? <CheckCircle size={16} color="#4caf50" /> : <AlertCircle size={16} color="#ff9800" />}
              <span>
                {reviewData.organization
                  ? t('step12.fields.organization', { name: reviewData.organization.name })
                  : t('step12.fields.organizationNotCreated')}
              </span>
            </div>
            {reviewData.clinicalStructure && (
              <>
                <div className={`review-item ${getStatus(reviewData.clinicalStructure.locations >= 1)}`}>
                  {reviewData.clinicalStructure.locations >= 1 ? <CheckCircle size={16} color="#4caf50" /> : <AlertCircle size={16} color="#ff9800" />}
                  <span>{t('step12.fields.locations', { count: reviewData.clinicalStructure.locations })}</span>
                </div>
                <div className={`review-item ${getStatus(reviewData.clinicalStructure.buildings >= 1)}`}>
                  {reviewData.clinicalStructure.buildings >= 1 ? <CheckCircle size={16} color="#4caf50" /> : <AlertCircle size={16} color="#ff9800" />}
                  <span>{t('step12.fields.buildings', { count: reviewData.clinicalStructure.buildings })}</span>
                </div>
                <div className={`review-item ${getStatus(reviewData.clinicalStructure.floors >= 1)}`}>
                  {reviewData.clinicalStructure.floors >= 1 ? <CheckCircle size={16} color="#4caf50" /> : <AlertCircle size={16} color="#ff9800" />}
                  <span>{t('step12.fields.floors', { count: reviewData.clinicalStructure.floors })}</span>
                </div>
                <div className={`review-item ${getStatus(reviewData.clinicalStructure.departments >= 1)}`}>
                  {reviewData.clinicalStructure.departments >= 1 ? <CheckCircle size={16} color="#4caf50" /> : <AlertCircle size={16} color="#ff9800" />}
                  <span>{t('step12.fields.departments', { count: reviewData.clinicalStructure.departments })}</span>
                </div>
                <div className={`review-item ${getStatus(reviewData.clinicalStructure.rooms >= 1)}`}>
                  {reviewData.clinicalStructure.rooms >= 1 ? <CheckCircle size={16} color="#4caf50" /> : <AlertCircle size={16} color="#ff9800" />}
                  <span>{t('step12.fields.rooms', { count: reviewData.clinicalStructure.rooms })}</span>
                </div>
                <div className={`review-item ${getStatus(reviewData.clinicalStructure.beds >= 1)}`}>
                  {reviewData.clinicalStructure.beds >= 1 ? <CheckCircle size={16} color="#4caf50" /> : <AlertCircle size={16} color="#ff9800" />}
                  <span>{t('step12.fields.beds', { count: reviewData.clinicalStructure.beds })}</span>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="review-section">
          <h4>{t('step12.sections.personnel')}</h4>
          <div className="review-items">
            {reviewData.users && (
              <>
                <div className={`review-item ${getStatus(reviewData.users.management >= 1)}`}>
                  {reviewData.users.management >= 1 ? <CheckCircle size={16} color="#4caf50" /> : <AlertCircle size={16} color="#ff9800" />}
                  <span>{t('step12.fields.management', { count: reviewData.users.management })}</span>
                </div>
                <div className={`review-item ${getStatus(reviewData.users.icPersonnel >= 1)}`}>
                  {reviewData.users.icPersonnel >= 1 ? <CheckCircle size={16} color="#4caf50" /> : <AlertCircle size={16} color="#ff9800" />}
                  <span>{t('step12.fields.icPersonnel', { count: reviewData.users.icPersonnel })}</span>
                </div>
                <div className={`review-item ${getStatus(reviewData.users.administration >= 1)}`}>
                  {reviewData.users.administration >= 1 ? <CheckCircle size={16} color="#4caf50" /> : <AlertCircle size={16} color="#ff9800" />}
                  <span>{t('step12.fields.administration', { count: reviewData.users.administration })}</span>
                </div>
                <div className="review-item">
                  <span>{t('step12.fields.totalUsers', { count: reviewData.users.total })}</span>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="review-section">
          <h4>{t('step12.sections.ipcSetup')}</h4>
          <div className="review-items">
            <div className={`review-item ${getStatus(reviewData.biologicalEntities >= 5)}`}>
              {reviewData.biologicalEntities >= 5 ? <CheckCircle size={16} color="#4caf50" /> : <AlertCircle size={16} color="#ff9800" />}
              <span>{t('step12.fields.biologicalEntities', { count: reviewData.biologicalEntities })}</span>
            </div>
          </div>
        </div>

        <div className="review-section">
          <h4>{t('step12.sections.operationalFoundation')}</h4>
          <div className="review-items">
            <div className={`review-item ${getStatus(reviewData.policyFrameworks >= 1)}`}>
              {reviewData.policyFrameworks >= 1 ? <CheckCircle size={16} color="#4caf50" /> : <AlertCircle size={16} color="#ff9800" />}
              <span>{t('step12.fields.policyFrameworks', { count: reviewData.policyFrameworks })}</span>
            </div>
            <div className={`review-item ${getStatus(reviewData.processes >= 3)}`}>
              {reviewData.processes >= 3 ? <CheckCircle size={16} color="#4caf50" /> : <AlertCircle size={16} color="#ff9800" />}
              <span>{t('step12.fields.processes', { count: reviewData.processes })}</span>
            </div>
          </div>
        </div>

        <div className="review-section">
          <h4>{t('step12.sections.protocolsRisks')}</h4>
          <div className="review-items">
            <div className="review-item">
              <span>{t('step12.fields.protocols', { count: reviewData.protocols })}</span>
            </div>
            <div className="review-item">
              <span>{t('step12.fields.risksConfigureLater')}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="review-summary" style={{ marginTop: '32px', padding: '20px', backgroundColor: '#e8f0fe', borderRadius: '8px' }}>
        <h4>{t('step12.summaryTitle')}</h4>
        <p>{t('step12.summaryText')}</p>
      </div>
    </div>
  );
};

export default Step12Review;
